import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  delay?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  decimals?: number;
  showPlus?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'hero'; // Hero variant for large, prominent displays
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  duration = 1.5,
  delay = 0,
  prefix = '$',
  suffix = '',
  className = '',
  decimals = 2,
  showPlus = false,
  size = 'md'
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Intersection Observer to trigger animation when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  // Animate the counter when visible
  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed - delay * 1000) / (duration * 1000), 1);
      
      if (progress < 0) {
        requestAnimationFrame(animate);
        return;
      }

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (value - startValue) * easeOutQuart;
      
      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, delay, isVisible]);

  // Format the display value
  const formatValue = (num: number) => {
    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    
    if (showPlus && num > 0) {
      return `+${formatted}`;
    }
    
    return formatted;
  };

  // Size-based styling - optimal sizing for all screen sizes
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg md:text-xl',
    hero: 'text-2xl md:text-3xl lg:text-4xl font-extrabold' // Reduced from text-5xl max
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.5, delay }}
      className={`font-bold theme-text ${sizeClasses[size]} ${className}`}
    >
      <motion.span
        key={displayValue} // Re-render when value changes
        initial={{ opacity: 0.7 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {prefix}{formatValue(displayValue)}{suffix}
      </motion.span>
    </motion.div>
  );
};

export default AnimatedCounter;
